import type { UnknownRecord } from "@/utils/type";

import type { ServerAlmType } from "../serverModel";
import type { RpcBackend } from "./RpcBackend";

export class SwitchingRpcBackend implements RpcBackend {
  constructor(
    private backends: RpcBackend[],
    private choose: (path: string) => number,
  ) {}

  get calls(): string[] {
    return this.backends.flatMap((backend) => backend.calls);
  }

  init(sessionId: string, almType?: ServerAlmType) {
    this.backends.forEach((backend) => backend.init(sessionId, almType));
  }

  /** Resolve the backend from kwargs.useBackend, stripping it before forwarding to the call */
  private route(path: string, kwargs?: UnknownRecord) {
    const { useBackend, ...rest } = kwargs ?? {};
    const index = (useBackend as number | undefined) ?? this.choose(path);
    return { backend: this.backends[index], rest: Object.keys(rest).length ? rest : undefined };
  }

  backend(path: string, kwargs?: UnknownRecord) {
    const useBackend = kwargs?.useBackend as number | undefined;
    return this.backends[useBackend ?? this.choose(path)];
  }

  roomCall<T>(path: string, args?: unknown[]): Promise<T> {
    return this.backend(path).roomCall(path, args);
  }

  sessionCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.backend(path).sessionCall(path, args, kwargs);
  }

  staticSessionCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.backend(path).staticSessionCall(path, args, kwargs);
  }

  serverCall<T>(
    path: string,
    args: unknown[],
    kwargs?: UnknownRecord,
    timeout?: number,
  ): Promise<T> {
    const { backend, rest } = this.route(path, kwargs);
    return backend.serverCall(path, args, rest, timeout);
  }

  statusCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord): Promise<T> {
    const { backend, rest } = this.route(path, kwargs);
    return backend.statusCall(path, args, rest);
  }

  boardCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.backend(path).boardCall(path, args, kwargs);
  }

  stickyCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.backend(path).stickyCall(path, args, kwargs);
  }

  flexBoardCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.backend(path).flexBoardCall(path, args, kwargs);
  }

  userCall<T>(path: string): Promise<T> {
    return this.backend(path).userCall(path);
  }

  adminCall<T>(path: string, args?: unknown[]): Promise<T> {
    return this.backend(path).adminCall(path, args);
  }

  almCall<T>(path: string, args?: unknown[]): Promise<T> {
    return this.backend(path).almCall(path, args);
  }

  almToolCall<T>(path: string, args: unknown[], timeout?: number): Promise<T> {
    return this.backend(path).almToolCall(path, args, timeout);
  }

  companyCall<T>(path: string, args: unknown[] = []): Promise<T> {
    return this.backend(path).companyCall(path, args);
  }
}
